/**
 * Mise à jour partielle d'une fiche client (super-admin).
 *
 * Le corps envoyé ne contient que les champs modifiés : côté API, un champ absent veut dire
 * « laisse la valeur stockée tranquille » et un champ à `null` veut dire « efface-la ». C'est cette
 * distinction qui fait qu'une correction de nom ne peut pas emporter avec elle le reste de la fiche,
 * contrairement au PUT qui réécrit tout ce qu'il reçoit.
 */
import { api } from "../api/api";

/**
 * Champs éditables depuis le tiroir de correction.
 *
 * Volontairement étroit : le tiroir existe pour débloquer une fiche que le formulaire complet
 * refuse d'enregistrer, pas pour le remplacer. Le corps du PATCH accepté par l'API est plus large
 * (notes, interrupteurs, dernière intervention) — ces champs restent sur le formulaire complet.
 *
 * L'identifiant Odoo n'est pas ici : il ne passe pas par le PATCH mais par son propre verbe.
 */
export interface CustomerPatchDraft {
  name: string;
  nip: string;
}

/** Champs identifiants : vides, ils valent « pas de valeur », donc `null` et non `""`. */
const IDENTIFIER_FIELDS = ["name", "nip"] as const;

const asText = (value: unknown): string => (value === null || value === undefined ? "" : String(value));

/**
 * Construit le corps du PATCH : uniquement ce qui a bougé entre la fiche chargée et le formulaire.
 *
 * @param baseline - fiche telle que l'API l'a renvoyée à l'ouverture du tiroir
 * @param edited - état courant du formulaire
 */
export const buildCustomerPatch = (
  baseline: EasyTech.Customer,
  edited: CustomerPatchDraft
): Record<string, unknown> => {
  const patch: Record<string, unknown> = {};

  IDENTIFIER_FIELDS.forEach((field) => {
    const next = edited[field].trim();
    if (next === asText(baseline[field]).trim()) return;
    patch[field] = next === "" ? null : next;
  });

  return patch;
};

/** Charge la fiche complète : la liste ne renvoie qu'une réponse « simple », incomplète. */
export const getCustomerForPatch = (id: string): Promise<EasyTech.Customer> =>
  api.get(`admin/customers/${id}`) as Promise<EasyTech.Customer>;

export const patchCustomer = (
  id: string,
  body: Record<string, unknown>
): Promise<EasyTech.Customer> =>
  api.patch(`admin/customers/${id}`, body) as Promise<EasyTech.Customer>;
